import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, ArrowUpRight } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { NAV } from "@/data/site";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 backdrop-blur-xl ${
        scrolled ? "bg-background/95 border-b border-border shadow-sm" : "bg-background/80 border-b border-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="shrink-0">
          <Logo markSize={38} tone="dark" />
        </Link>

        <div className="hidden xl:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive ? "text-primary" : "text-muted-foreground hover:text-primary"
                }`
              }
            >
              {n.short || n.label}
            </NavLink>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Button asChild size="sm" className="hidden sm:inline-flex bg-primary hover:bg-accent hover:text-primary text-white font-semibold rounded-full px-5" data-testid="nav-cta-submit-site">
            <Link to="/site-submission">SUBMIT A SITE</Link>
          </Button>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="xl:hidden text-primary" data-testid="nav-menu-toggle">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-background border-l border-border w-[300px] p-0">
              <div className="p-6 border-b border-border">
                <Logo markSize={36} tone="dark" />
              </div>
              <div className="flex flex-col p-4">
                {NAV.map((n) => (
                  <Link
                    key={n.to}
                    to={n.to}
                    data-testid={`mobile-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="flex items-center justify-between px-3 py-3 text-base text-foreground hover:text-primary border-b border-border"
                  >
                    {n.label} <ArrowUpRight className="h-4 w-4 opacity-40" />
                  </Link>
                ))}
                <Button asChild className="mt-5 bg-primary hover:bg-accent hover:text-primary text-white font-semibold rounded-full">
                  <Link to="/site-submission">SUBMIT A SITE</Link>
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
}
